function NorthStar({ rating, career }) {
  const r = rating || {};
  const c = career || {};
  const provisional = !r.has_rating || r.is_provisional;
  return (
    <div data-testid="north-star-panel" className="rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-6">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-4 flex items-center gap-2">
        <TrendingUp className="w-4 h-4" /> My Competitive Identity
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">ArenaBook Rating</div>
          {r.has_rating ? (
            <div data-testid="ns-rating" className="font-heading text-4xl font-black text-[#E5B842] tabular-nums leading-none">
              {Math.round(r.rating)}
              <span className="text-[11px] font-mono text-slate-500 ml-1">±{Math.round(r.rd)}</span>
            </div>
          ) : (
            <div className="font-heading text-2xl font-black text-slate-500">1500</div>
          )}
          <div className="mt-1 flex items-center gap-2">
            {provisional && <ProvisionalTag matches={r.matches_count} threshold={r.official_progress?.results_required} />}
            <InactivityBadge active={r.active_ranking} />
          </div>
          <OfficialProgress progress={r.official_progress} status={r.status} testid="ns-official-progress" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1 flex items-center gap-1"><Globe className="w-3 h-3" /> World Rank</div>
          {!provisional && r.world_rank ? (
            <div data-testid="ns-world-rank" className="font-heading text-4xl font-black text-slate-50 tabular-nums leading-none flex items-baseline gap-2">
              #{r.world_rank.position}<RankChange change={r.world_rank.change} />
            </div>
          ) : (<div className="font-heading text-xl font-bold text-slate-600">—</div>)}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1 flex items-center gap-1"><Flag className="w-3 h-3" /> Greece Rank</div>
          {!provisional && r.national_rank ? (
            <div data-testid="ns-greece-rank" className="font-heading text-4xl font-black text-slate-50 tabular-nums leading-none flex items-baseline gap-2">
              #{r.national_rank.position}<RankChange change={r.national_rank.change} />
            </div>
          ) : (<div className="font-heading text-xl font-bold text-slate-600">—</div>)}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1 flex items-center gap-1"><Swords className="w-3 h-3" /> Duels (W-L-D)</div>
          <div data-testid="ns-duels" className="font-heading text-2xl font-black text-slate-50 tabular-nums leading-none">
            {c.duels_won || 0}-{c.duels_lost || 0}-{c.duels_split || 0}
          </div>
          <div className="text-[11px] font-mono text-slate-500 mt-1 flex items-center gap-1">
            <Target className="w-3 h-3" /> {c.team_points_generated || 0} team points
          </div>
        </div>
      </div>
    </div>
  );
}


import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Plus, Check, X, Swords, Clock, TrendingUp, Globe, Flag, Target, Users, Crown } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageTitle, Card, GoldButton, GhostButton, Loader } from "../components/UI";
import { PlayerPassport } from "../components/PlayerPassport";
import { Strike5Card, DisciplineCard, OverallCard, WalletWidget } from "../components/DisciplineCards";
import { StatusBadge } from "../components/Badges";
import { RankChange, ProvisionalTag, InactivityBadge, OfficialProgress } from "../components/Rating";
import { MatchStatusBadge } from "../components/Arena";
import { ClubCrest } from "../components/ClubCrest";
import { SeasonShareButton } from "../components/StandingsShare";
import { Input } from "./Login";

function MyTeamCard({ clubId, viewerWsbfId }) {
  const [club, setClub] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get(`/clubs/${clubId}`)
      .then(({ data }) => { if (!cancel) setClub(data); })
      .catch(() => { if (!cancel) setClub({ _err: true }); });
    return () => { cancel = true; };
  }, [clubId]);
  if (!club) return <div className="text-xs font-mono text-slate-600">Loading…</div>;
  if (club._err) return <span className="text-slate-500 italic text-sm">—</span>;
  const isOwner = club.owner?.wsbf_player_id && club.owner.wsbf_player_id === viewerWsbfId;
  return (
    <>
    <Link to={`/club/${club.id}`} className="block group" data-testid="dashboard-myteam-link" style={{ transition: "transform 0.15s ease" }}>
      <div className="flex items-start gap-3">
        <ClubCrest club={club} size={44} className="shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-heading font-black uppercase text-slate-50 group-hover:text-[#E5B842] text-sm truncate" style={{ transition: "color 0.15s ease" }} data-testid="dashboard-myteam-name">
            {club.short_name || club.name}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 truncate">{club.city}</div>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider bg-[#141414] border border-[#2b2512] text-slate-300" data-testid="dashboard-myteam-status">{club.status}</span>
            {isOwner ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider bg-[#E5B842]/15 border border-[#E5B842]/40 text-[#E5B842]" data-testid="dashboard-myteam-role">
                <Crown className="w-2.5 h-2.5" />Owner
              </span>
            ) : (
              <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider bg-sky-950/40 border border-sky-500/30 text-sky-300" data-testid="dashboard-myteam-role">Player</span>
            )}
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider bg-[#141414] border border-[#2b2512] text-slate-400" data-testid="dashboard-myteam-members">
              <Users className="w-2.5 h-2.5" />{club.roster?.count ?? 0}/{club.roster?.max ?? "—"}
            </span>
          </div>
        </div>
      </div>
    </Link>
    {isOwner && <OwnerSeasonShare clubId={club.id} />}
    </>
  );
}

// Owner-only: season card PNG for the club's most recent public competition.
function OwnerSeasonShare({ clubId }) {
  const [comp, setComp] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get(`/clubs/${clubId}/competitions`)
      .then(({ data }) => { if (!cancel) setComp((data.competitions || []).find((c) => (c.standings || []).length) || false); })
      .catch(() => { if (!cancel) setComp(false); });
    return () => { cancel = true; };
  }, [clubId]);
  if (!comp) return null;
  const us = comp.standings.find((r) => r.club_id === clubId);
  return (
    <div className="mt-3 flex items-center justify-between gap-2 border-t border-[#2b2512] pt-2" data-testid="dashboard-myteam-season">
      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 truncate">{comp.name}</span>
      <SeasonShareButton comp={comp} us={us} fixtures={comp.fixtures || []} />
    </div>
  );
}

function UpcomingMatchCard({ m }) {
  const open = ["SUBMISSION_OPEN", "SUBMISSION_IN_PROGRESS"].includes(m.status);
  const teamCount = m.my_side === "HOME" ? m.home_coupon_count : m.away_coupon_count;
  return (
    <Card className="p-5 foil-border" data-testid={`upcoming-match-${m.public_match_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[#E5B842] font-heading font-bold uppercase tracking-wide text-sm">
          <Swords className="w-4 h-4" /> Upcoming Team Match
        </div>
        <MatchStatusBadge status={m.status} />
      </div>
      <Link to={`/match/${m.id}`} className="font-heading text-xl font-black uppercase tracking-tight text-slate-50 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
        {m.home_club?.name} <span className="text-slate-600">vs</span> {m.away_club?.name}
      </Link>
      <div className="text-[11px] font-mono text-slate-500 mt-1 flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" /> {m.public_match_id} · you play for {m.my_side}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="rounded-lg bg-[#0f0f0f] border border-[#2b2512] p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">My coupons</div>
          <div className="font-heading text-2xl font-black text-slate-50">{m.my_coupon_count}<span className="text-sm text-slate-500">/3</span></div>
        </div>
        <div className="rounded-lg bg-[#0f0f0f] border border-[#2b2512] p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Team coupons</div>
          <div className="font-heading text-2xl font-black text-slate-50">{teamCount}<span className="text-sm text-slate-500">/11</span></div>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        {open && m.my_coupon_count < 3 && (
          <Link to={`/match/${m.id}/play`} className="flex-1"><GoldButton className="w-full" data-testid={`create-coupon-${m.public_match_id}`}>Create ArenaBook coupon</GoldButton></Link>
        )}
        <Link to={`/match/${m.id}`} className={open && m.my_coupon_count < 3 ? "" : "flex-1"}><GhostButton className="w-full">View match</GhostButton></Link>
      </div>
    </Card>
  );
}

export default function PlayerDashboard() {
  const { player, refresh } = useAuth();
  const [invites, setInvites] = useState(null);
  const [matches, setMatches] = useState([]);
  const [careerData, setCareerData] = useState(null);
  const [reqName, setReqName] = useState("");
  const [reqCountry, setReqCountry] = useState("");
  const [reqCity, setReqCity] = useState("");
  const [reqDesc, setReqDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [countries, setCountries] = useState([]);

  const loadInvites = useCallback(() => {
    api.get("/invitations/mine").then(({ data }) => setInvites(data.invitations)).catch(() => setInvites([]));
  }, []);

  const loadRequests = useCallback(() => {
    api.get("/club-requests/mine").then(({ data }) => setMyRequests(data.requests || [])).catch(() => setMyRequests([]));
  }, []);

  useEffect(() => { loadInvites(); loadRequests(); }, [loadInvites, loadRequests]);

  useEffect(() => {
    api.get("/meta").then(({ data }) => {
      setCountries(data.countries || []);
      if (!reqCountry && data.countries?.length) setReqCountry(data.countries[0].iso3);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/me/matches").then(({ data }) => setMatches(data.matches)).catch(() => setMatches([]));
    api.get("/me/career").then(({ data }) => setCareerData(data)).catch(() => setCareerData(null));
  }, [player]);

  const respond = async (id, action) => {
    try {
      await api.post(`/invitations/${id}/${action}`);
      toast.success(`Invitation ${action}ed`);
      loadInvites();
      await refresh();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/club-requests", {
        club_name: reqName, country: reqCountry,
        city: reqCity || undefined, description: reqDesc || undefined,
      });
      toast.success("Club request submitted — an admin will review it.");
      setReqName(""); setReqCity(""); setReqDesc("");
      loadRequests();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (!player) return <Loader />;

  return (
    <Page>
      <PageTitle eyebrow={`Welcome, ${player.display_name}`} title="My Player ID" subtitle="Your official WSTF credential and club activity." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 min-w-0">
          <PlayerPassport player={player} club={player.club} rating={careerData?.rating} career={careerData?.career} />
        </div>

        <div className="lg:col-span-2 min-w-0 space-y-6">
          <NorthStar rating={careerData?.rating} career={careerData?.career} />
          <WalletWidget wallet={careerData?.wallet} />
          <OverallCard overall={careerData?.overall} />
          <div className="grid sm:grid-cols-2 gap-4" data-testid="my-disciplines">
            <Strike5Card strike5={careerData?.strike5} />
            <DisciplineCard block={careerData?.challenge} label="Challenge" accent="text-fuchsia-300" testid="challenge-rating-card" />
          </div>
          {matches.length > 0 && (
            <div className="space-y-3" data-testid="upcoming-matches">
              {matches.map((m) => <UpcomingMatchCard key={m.id} m={m} />)}
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">My Status</div>
              <StatusBadge status={player.status} />
            </Card>
            <Card className="p-5" data-testid="dashboard-myteam-card">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">My Team</div>
              {player.club_id ? <MyTeamCard clubId={player.club_id} viewerWsbfId={player.wsbf_player_id} /> : (
                <div className="space-y-1" data-testid="dashboard-myteam-free-agent">
                  <span className="text-slate-500 italic text-sm">Free agent</span>
                  <div className="text-[10px] font-mono text-slate-600">
                    <Link to="/club-builder" className="text-[#E5B842] hover:underline">Create your club →</Link>
                  </div>
                </div>
              )}
            </Card>
            <Card className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">Public ID</div>
              <span className="font-mono text-[#E5B842] font-bold tracking-wider text-sm">{player.wsbf_player_id}</span>
            </Card>
          </div>

          <div>
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3 flex items-center gap-2"><Mail className="w-5 h-5 text-[#E5B842]" /> My Invitations</h2>
            {!invites ? <Loader label="Loading…" /> : invites.length === 0 ? (
              <Card className="p-6 text-slate-500 text-sm" data-testid="no-invitations">No pending invitations.</Card>
            ) : (
              <div className="space-y-3" data-testid="invitations-list">
                {invites.map((inv) => (
                  <Card key={inv.id} className="p-4 flex items-center justify-between gap-3" data-testid={`invitation-${inv.id}`}>
                    <div>
                      <div className="font-heading font-bold uppercase tracking-tight text-slate-50">{inv.club_name}</div>
                      <div className="text-xs text-slate-500 font-mono">invited you to join the roster</div>
                    </div>
                    <div className="flex gap-2">
                      <GoldButton data-testid={`accept-invite-${inv.id}`} onClick={() => respond(inv.id, "accept")}><Check className="w-4 h-4" /> Accept</GoldButton>
                      <GhostButton data-testid={`decline-invite-${inv.id}`} onClick={() => respond(inv.id, "decline")}><X className="w-4 h-4" /> Decline</GhostButton>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {myRequests.length > 0 && (
            <div data-testid="my-club-requests">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3 flex items-center gap-2"><Clock className="w-5 h-5 text-[#E5B842]" /> My Club Requests</h2>
              <div className="space-y-3">
                {myRequests.map((r) => {
                  const style = r.status === "APPROVED" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                    : r.status === "REJECTED" ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
                    : "bg-amber-500/15 text-amber-300 border-amber-500/30";
                  return (
                    <Card key={r.id} className="p-4" data-testid={`my-request-${r.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="font-heading font-bold uppercase tracking-tight text-slate-50 break-words">{r.club_name}</div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${style}`} data-testid={`my-request-status-${r.id}`}>{r.status}</span>
                      </div>
                      {r.status === "REJECTED" && r.reject_reason && (
                        <div className="text-xs text-rose-300/90 mt-2 break-words" data-testid={`my-request-reason-${r.id}`}>Reason: {r.reject_reason}</div>
                      )}
                      {r.status === "APPROVED" && r.created_wsbf_club_id && (
                        <div className="text-xs text-emerald-300/90 mt-2 font-mono">Club created: {r.created_wsbf_club_id}</div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {!player.club && !myRequests.some((r) => ["PENDING", "APPROVING"].includes(r.status)) && (
            <div>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3 flex items-center gap-2"><Plus className="w-5 h-5 text-[#E5B842]" /> Request a Club</h2>
              <Card className="p-6">
                <p className="text-sm text-slate-400 mb-4">Submit a club-creation request. A WSTF admin will review it; once approved, your club is created and you become its founder.</p>
                <form onSubmit={submitRequest} className="grid sm:grid-cols-2 gap-4">
                  <Input label="Club name" value={reqName} onChange={setReqName} testid="request-club-name" placeholder="Athens Arena" />
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Country</label>
                    <select
                      data-testid="request-club-country"
                      value={reqCountry}
                      onChange={(e) => setReqCountry(e.target.value)}
                      className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60"
                    >
                      {countries.map((c) => <option key={c.iso3} value={c.iso3}>{c.name}</option>)}
                    </select>
                  </div>
                  <Input label="City (optional)" value={reqCity} onChange={setReqCity} testid="request-club-city" placeholder="Athens" />
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Description (optional)</label>
                    <input
                      data-testid="request-club-description"
                      value={reqDesc}
                      onChange={(e) => setReqDesc(e.target.value)}
                      placeholder="A short note for the admin"
                      className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <GoldButton type="submit" disabled={submitting || reqName.trim().length < 3 || !reqCountry} data-testid="request-club-submit">
                      {submitting ? "Submitting…" : "Submit club request"}
                    </GoldButton>
                  </div>
                </form>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
